import {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { uniqBy } from 'lodash';

import { resourceIconMap, resourceNameMap } from '@dbt-labs/dbt-dag';
import {
  FileTreeItemType,
  Ryecon,
  RyeconColorDbt,
  TreeNodeValues,
  useOpenDirectories,
} from '@dbt-labs/sourdough';

import { useLinkToExplorer } from '@/dbt-explorer';
import { useFavoriteProjectIds } from '@/hooks/admin/favoriteProjects';
import { Project, useProjectLazy, useProjectsLazy } from '@/hooks/admin/project';
import { getDefaultEnvironmentIds } from '@/hooks/admin/types';
import {
  aggregateEnvironmentSummary,
  EnvironmentSummary,
  useEnvironmentSummariesLazy,
} from '@/hooks/discovery/environmentSummary';
import { useValidatedParams } from '@/hooks/params';
import { ProjectEntry } from '@/hooks/useAllProjects';
import { truthy } from '@/util/array';
import { useDbtCloudAccount } from '../DbtCloudProvider';
import {
  DBT_ASSETS_ROOT_NODE_NAME,
  DBT_ROOT_NODE_LABEL,
  PROJECT_PAGE_SIZE,
} from './const';
import { DbtInternalTree, DbtTreeData } from './types';
import {
  generateFileWithLogo,
  generateFolderWithIcon,
  mergeTreeItems,
  parseProject,
  parseResourceItem,
  scrollToPath,
  sortDbtAssets,
  useMenuActions,
} from './utils';

type SummaryType = keyof NonNullable<ProjectEntry['projectSummary']>;

const validSummaryTypes = [
  'models',
  'sources',
  'tests',
  'exposures',
  'groups',
  'metrics',
  'semanticModels',
  'seeds',
  'macros',
  'snapshots',
  'savedQuerys',
];

const summaryMapping: Partial<Record<SummaryType, { ryecon: Ryecon; label: string }>> =
  {
    metrics: {
      ryecon: resourceIconMap.metric,
      label: resourceNameMap.metric,
    },
    exposures: {
      ryecon: resourceIconMap.exposure,
      label: resourceNameMap.exposure,
    },
    snapshots: {
      ryecon: resourceIconMap.snapshot,
      label: resourceNameMap.snapshot,
    },
    seeds: {
      ryecon: resourceIconMap.seed,
      label: resourceNameMap.seed,
    },
    tests: {
      ryecon: resourceIconMap.test,
      label: resourceNameMap.test,
    },
    sources: {
      ryecon: resourceIconMap.source,
      label: resourceNameMap.source,
    },
    savedQuerys: {
      ryecon: resourceIconMap.saved_query,
      label: resourceNameMap.saved_query,
    },
    semanticModels: {
      ryecon: resourceIconMap.semantic_model,
      label: resourceNameMap.semantic_model,
    },
    groups: {
      ryecon: resourceIconMap.group,
      label: resourceNameMap.group,
    },
    macros: {
      ryecon: resourceIconMap.macro,
      label: resourceNameMap.macro,
    },
    models: {
      ryecon: resourceIconMap.model,
      label: resourceNameMap.model,
    },
  };

const getResourceType = (resourceType: string | undefined) => {
  if (!resourceType) return undefined;
  if (resourceType.charAt(resourceType.length - 1) !== 's') {
    return `${resourceType}s`;
  }
  return resourceType;
};

export const useDbtAssetsTree = ({
  allProjects,
  setAllProjects,
  projectsPageInfo,
  setProjectsPageInfo,
  viewParam,
  selectedEnvironmentType,
}: DbtInternalTree): DbtTreeData => {
  const { accountId } = useDbtCloudAccount();
  const { projectId, resourceType, uniqueId } = useValidatedParams();

  const { getMenuActions } = useMenuActions();
  const { favoriteProjectIds } = useFavoriteProjectIds(accountId ?? 0);

  const navigate = useNavigate();
  const location = useLocation();
  const accountOverview = useLinkToExplorer('multiProject');
  const dbtProject = useLinkToExplorer('project');
  const resourceFilterView = useLinkToExplorer('resourceFilter');
  const [selectedFile, setSelectedFile] = useState<string>();
  const [selectedFolder, setSelectedFolder] = useState<string | undefined>(undefined);
  const [isInitialized, setIsInitialized] = useState(false);
  const prevEnvironmentTypeRef = useRef(selectedEnvironmentType);

  const isActive = useMemo(() => {
    return viewParam === 'assetList' || !viewParam;
  }, [viewParam]);

  const [dbtTree, setDbtTree] = useState<FileTreeItemType[]>([
    generateFolderWithIcon(DBT_ASSETS_ROOT_NODE_NAME, {
      iconOverride: {
        ryecon: RyeconColorDbt,
        label: DBT_ROOT_NODE_LABEL,
      },
      name: DBT_ROOT_NODE_LABEL,
      data: {
        pagination: {
          page: 0,
          hasMore: false,
          total: 0,
          isLoading: false,
        },
      },
    }),
  ]);

  const [, getProject] = useProjectLazy();
  const [projects, getProjects] = useProjectsLazy();
  const [, getEnvironmentSummaries] = useEnvironmentSummariesLazy();

  const { openDirectories, setOpenDirectories } = useOpenDirectories({
    items: dbtTree,
    activeFilePath: selectedFile,
    rootNodeName: DBT_ASSETS_ROOT_NODE_NAME,
  });

  const generateDbtFiles = useCallback(
    (summaries: EnvironmentSummary[], projectId: number, projectName: string) => {
      const aggregatedSummary = aggregateEnvironmentSummary(summaries, projectName);

      const resourceCounts: Record<string, number> = {
        models: aggregatedSummary.model,
        sources: aggregatedSummary.source,
        tests: aggregatedSummary.test,
        exposures: aggregatedSummary.exposure,
        groups: aggregatedSummary.groups,
        metrics: aggregatedSummary.metric,
        semanticModels: aggregatedSummary.semantic_model,
        seeds: aggregatedSummary.seed,
        macros: aggregatedSummary.macro,
        snapshots: aggregatedSummary.snapshot,
        savedQuerys: aggregatedSummary.saved_query,
      };

      return validSummaryTypes.map((resourceType) => {
        const count = resourceCounts[resourceType as SummaryType] || 0;
        const mapping = summaryMapping[resourceType as SummaryType];
        const id = `${DBT_ASSETS_ROOT_NODE_NAME}/${projectId}/${resourceType}`;
        return generateFileWithLogo(id, {
          iconOverride: {
            ryecon: mapping?.ryecon ?? resourceIconMap.unknown,
            label: mapping?.label ?? '',
          },
          name: mapping?.label,
          data: {
            info: {
              text: count ? String(count) : '',
            },
            tooltipText: mapping?.label,
          },
        });
      });
    },
    [],
  );

  const updateSummariesForEnvironment = useCallback(async () => {
    const updatedNodes = await Promise.all(
      openDirectories.map(async (path) => {
        const node = dbtTree.find((node) => node.id === path);
        const project = allProjects.find(
          (project) => project.id === Number(path.split('/').pop()),
        );
        if (!node || !project) return;
        const environmentIds = getDefaultEnvironmentIds(project.environments || [], [
          selectedEnvironmentType,
        ]);

        const summaries = await getEnvironmentSummaries({
          accountId: accountId ?? 0,
          environmentIds,
        });

        if (!summaries?.data?.length) return;

        const projectName =
          allProjects.find((project) => project.id === projectId)?.name || '';
        const fileTreeItems = generateDbtFiles(
          summaries.data,
          project.id!,
          projectName,
        );

        return fileTreeItems;
      }),
    );
    const normalizedUpdatedNodes = updatedNodes.filter(truthy).flat();

    setDbtTree((prev) => {
      // remove all child nodes that are in the open directories except from the root node
      const newTree = prev.filter((node) => {
        return (
          node.parent === DBT_ASSETS_ROOT_NODE_NAME ||
          node.id === DBT_ASSETS_ROOT_NODE_NAME ||
          !openDirectories.includes(node.parent)
        );
      });
      return uniqBy([...newTree, ...normalizedUpdatedNodes], 'id').filter(truthy);
    });
  }, [
    openDirectories,
    dbtTree,
    allProjects,
    selectedEnvironmentType,
    getEnvironmentSummaries,
    accountId,
    generateDbtFiles,
    projectId,
  ]);

  useEffect(() => {
    if (!isActive) return;
    if (prevEnvironmentTypeRef.current !== selectedEnvironmentType) {
      prevEnvironmentTypeRef.current = selectedEnvironmentType;
      updateSummariesForEnvironment();
    }
  }, [selectedEnvironmentType, isActive, updateSummariesForEnvironment]);

  const onSort = (a: string, b: string) => {
    return sortDbtAssets(a, b, allProjects, validSummaryTypes);
  };

  const setTreeStateForPath = useCallback(
    (path: string, props: Partial<TreeNodeValues['pagination']>) => {
      setDbtTree((prev) => {
        const newTree = [...prev];
        const updatedNode = newTree.find((node) => node.id === path);
        if (!updatedNode) return prev;
        updatedNode.data.pagination = {
          hasMore: props?.hasMore ?? updatedNode.data.pagination?.hasMore ?? false,
          page: props?.page ?? updatedNode.data.pagination?.page ?? 0,
          total: props?.total ?? updatedNode.data.pagination?.total ?? 0,
          isLoading:
            props?.isLoading ?? updatedNode.data.pagination?.isLoading ?? false,
        };
        return uniqBy(newTree, 'id');
      });
    },
    [],
  );

  const navigateToProject = useCallback(
    (folder: string | undefined) => {
      if (!folder) return;
      if (folder === DBT_ASSETS_ROOT_NODE_NAME) {
        navigate(
          accountOverview({
            accountId: accountId ?? 0,
          }).concat(location.search),
        );
        return;
      }
      const parts = folder.split('/');
      if (parts.length < 2) return;
      const projectId = parts.pop();
      navigate(
        dbtProject({
          accountId: accountId ?? 0,
          projectId: Number(projectId),
          environmentType: selectedEnvironmentType,
        }).concat(location.search),
      );
    },
    [
      navigate,
      dbtProject,
      accountId,
      selectedEnvironmentType,
      location.search,
      accountOverview,
    ],
  );

  const generateResourceFileTreeItems = useCallback(
    async (path: string, environmentIds: number[]) => {
      const fileTreeItems: FileTreeItemType[] = [];
      const pathParts = path.split('/');
      if (pathParts.length >= 2) {
        if (!environmentIds) {
          // get the parent node
          const node = dbtTree.find((node) => node.id === path);
          if (!node) {
            setTreeStateForPath(path, { isLoading: false, hasMore: false });
            return fileTreeItems;
          }

          // extract environment IDs
          environmentIds =
            (node.data.info as { environmentIds?: number[] })?.environmentIds || [];
        }

        if (environmentIds.length === 0) {
          // if no environment IDs, we can't fetch summaries
          setTreeStateForPath(path, { isLoading: false, hasMore: false });
          return fileTreeItems;
        }

        const summaries = await getEnvironmentSummaries({
          accountId: accountId ?? 0,
          environmentIds,
        });

        // request errored, so we populate the tree with empty counts
        if (!summaries.data) {
          setTreeStateForPath(path, { isLoading: false, hasMore: false });
          return fileTreeItems;
        }

        if (!projectId) return fileTreeItems;

        const projectInfo = parseProject(path);
        if (projectInfo) {
          const projectId = projectInfo.projectId;
          const projectName =
            allProjects.find((project) => project.id === projectId)?.name || '';
          fileTreeItems.push(
            ...generateDbtFiles(summaries.data, projectId, projectName),
          );
        }
      }
      return fileTreeItems;
    },
    [
      accountId,
      allProjects,
      dbtTree,
      generateDbtFiles,
      getEnvironmentSummaries,
      projectId,
      setTreeStateForPath,
    ],
  );

  // update the tree with the favorite actions
  useEffect(() => {
    if (isActive && isInitialized) {
      setDbtTree((prev) => {
        const newTree = prev.map((node) => {
          // it could be a project or a resource
          const projectId = node.id.split('/').pop();
          const project = allProjects.find(
            (project) => project.id === Number(projectId),
          );
          if (!project) return node;

          return {
            ...node,
            data: {
              ...node.data,
              actions: getMenuActions({
                projectId: project.id!,
                type: 'project',
                platform: 'dbt',
                environmentId:
                  getDefaultEnvironmentIds(project?.environments ?? [], [
                    selectedEnvironmentType,
                  ])?.[0]?.toString() ?? '',
                name: project.name ?? '',
                fileTreeId: node.id,
              }),
            },
          };
        });
        return newTree;
      });
    }
    // we only need to check for the favorite projects and the active state
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoriteProjectIds, isActive, isInitialized]);

  const generateProjectFileTreeItems = useCallback(
    (projectData: Project[]) => {
      const fileTreeItems = projectData.map((project) => {
        const environmentIds = getDefaultEnvironmentIds(project.environments || [], [
          selectedEnvironmentType,
        ]);
        const id = `${DBT_ASSETS_ROOT_NODE_NAME}/${project.id}`;
        return generateFolderWithIcon(id, {
          name: project.name ?? '',
          data: {
            actions: getMenuActions({
              fileTreeId: id,
              projectId: project.id!,
              type: 'project',
              platform: 'dbt',
              name: project.name ?? '',
              environmentId:
                getDefaultEnvironmentIds(project?.environments ?? [], [
                  selectedEnvironmentType,
                ])?.[0]?.toString() ?? '',
            }),
            info: {
              environmentIds: environmentIds ?? [],
            },
            tooltipText: project.name,
            pagination: {
              page: 0,
              hasMore: false,
              total: 0,
              isLoading: false,
            },
          },
        });
      });
      return fileTreeItems;
    },
    [selectedEnvironmentType, getMenuActions],
  );

  const updateFileTree = useCallback(
    (
      currentData: {
        data: Project[] | EnvironmentSummary[];
        pageInfo?: { hasNextPage?: boolean; endCursor?: string; totalCount?: number };
      },
      path: string,
    ) => {
      if (!currentData || !currentData.data) return;

      let fileTreeItems: FileTreeItemType[] = [];
      if (path === DBT_ASSETS_ROOT_NODE_NAME) {
        const projectData = currentData.data as Project[];
        fileTreeItems = generateProjectFileTreeItems(projectData);
      } else {
        const environmentData = currentData.data as EnvironmentSummary[];
        const projectInfo = parseProject(path);
        if (projectInfo) {
          const projectId = projectInfo.projectId;
          const projectName =
            allProjects.find((project) => project.id === projectId)?.name || '';
          fileTreeItems = generateDbtFiles(environmentData, projectId, projectName);
        }
      }

      if (currentData.pageInfo?.hasNextPage) {
        setProjectsPageInfo((prev) => ({
          ...prev,
          hasNextPage: currentData.pageInfo?.hasNextPage ?? false,
          totalCount: currentData.pageInfo?.totalCount ?? 0,
          after: {
            ...prev.after,
            [path]: currentData.pageInfo?.endCursor || '',
          },
        }));
      }

      setDbtTree((prev) => {
        const newItems = [...prev, ...fileTreeItems];
        const updatedNode = newItems.find((node) => node.id === path);
        if (!updatedNode) return prev;
        const page = updatedNode.data.pagination?.page ?? 0;
        return uniqBy(
          [
            ...prev.filter((node) => node.id !== path),
            ...fileTreeItems,
            {
              ...updatedNode,
              data: {
                ...updatedNode.data,
                pagination: {
                  ...updatedNode.data.pagination,
                  hasMore: currentData.pageInfo?.hasNextPage ?? false,
                  total: currentData.pageInfo?.totalCount ?? 0,
                  isLoading: false,
                  page: page,
                },
              },
            },
          ],
          'id',
        );
      });
    },
    [allProjects, generateDbtFiles, generateProjectFileTreeItems, setProjectsPageInfo],
  );

  const generateChildrenForPath = useCallback(
    async (path: string, environmentIdsProp?: number[]): Promise<void> => {
      setTreeStateForPath(path, { isLoading: true, hasMore: false });

      if (path === DBT_ASSETS_ROOT_NODE_NAME) {
        const projects = await getProjects({
          accountId: accountId ?? 0,
          first: PROJECT_PAGE_SIZE,
          after: projectsPageInfo.after[DBT_ROOT_NODE_LABEL] || '',
          includeRelated: ['environments'],
        });
        if (!projects.data) {
          setTreeStateForPath(path, { isLoading: false, hasMore: false });
          return;
        }
        setAllProjects((prev) =>
          uniqBy([...prev, ...(projects.data as Project[])], 'id'),
        );
        setProjectsPageInfo((prev) => ({
          ...prev,
          hasNextPage: projects.pageInfo?.hasNextPage ?? false,
          totalCount: projects.pageInfo?.totalCount ?? 0,
          after: {
            ...prev.after,
            [DBT_ROOT_NODE_LABEL]: projects.pageInfo?.endCursor || '',
          },
        }));
        updateFileTree(
          {
            data: projects.data as Project[],
            pageInfo: projects.pageInfo,
          },
          path,
        );
      } else if (path.startsWith(`${DBT_ASSETS_ROOT_NODE_NAME}/`)) {
        const pathParts = path.split('/');
        if (pathParts.length >= 2) {
          let environmentIds = environmentIdsProp;
          if (!environmentIds) {
            // get the parent node
            const node = dbtTree.find((node) => node.id === path);
            if (!node) {
              setTreeStateForPath(path, { isLoading: false, hasMore: false });
              return;
            }

            // extract environment IDs
            environmentIds =
              (node.data.info as { environmentIds?: number[] })?.environmentIds || [];
          }

          if (environmentIds.length === 0) {
            // if no environment IDs, we can't fetch summaries
            setTreeStateForPath(path, { isLoading: false, hasMore: false });
            return;
          }

          const summaries = await getEnvironmentSummaries({
            accountId: accountId ?? 0,
            environmentIds,
          });

          // request errored, so we populate the tree with empty counts
          if (!summaries.data) {
            setTreeStateForPath(path, { isLoading: false, hasMore: false });
            return;
          }

          updateFileTree(
            {
              data: summaries.data,
              pageInfo: {
                hasNextPage: false,
                totalCount: summaries.data.length,
              },
            },
            path,
          );
        }
      }
    },
    [
      setTreeStateForPath,
      getProjects,
      accountId,
      projectsPageInfo,
      setAllProjects,
      updateFileTree,
      getEnvironmentSummaries,
      dbtTree,
      setProjectsPageInfo,
    ],
  );

  const handleSetSelectedFile = useCallback(
    (value: SetStateAction<string | undefined>) => {
      const newValue = typeof value === 'function' ? value(selectedFile) : value;
      setSelectedFile(newValue);
      const projectInfo = parseResourceItem(newValue);
      if (!projectInfo) return;
      navigate(
        resourceFilterView({
          accountId: accountId ?? 0,
          projectId: projectInfo.projectId,
          environmentType: selectedEnvironmentType,
          resourceType: projectInfo?.resourceType ?? 'model',
        }).concat(location.search),
      );
    },
    [
      selectedFile,
      navigate,
      resourceFilterView,
      accountId,
      selectedEnvironmentType,
      location.search,
    ],
  );

  const handleSetSelectedFolder = useCallback(
    (
      value: SetStateAction<string | undefined>,
      _event?: React.MouseEvent,
      isCollapseClick = false,
    ) => {
      const newValue = typeof value === 'function' ? value(selectedFolder) : value;
      if (!isCollapseClick) {
        setSelectedFolder(newValue);
        navigateToProject(newValue);
      }
    },
    [navigateToProject, selectedFolder],
  );

  const projectsLoaded = allProjects.length > 0;

  const openDirectoryIfNotOpen = useCallback(
    (path: string) => {
      if (!openDirectories.some((dir) => dir === path)) {
        setOpenDirectories((prev) => [...(prev ?? []), path]);
      }
    },
    [openDirectories, setOpenDirectories],
  );

  const navigateAndScrollToFile = useCallback(
    (path: string) => {
      if (resourceType || uniqueId) {
        const resourceTypeParam = getResourceType(
          resourceType || uniqueId?.resourceType,
        );
        setSelectedFile(`${path}/${resourceTypeParam}`);
      } else {
        setSelectedFolder(path);
      }
      scrollToPath(path, {
        parentSelector: '#side-navigation-container',
        useAbsolutePosition: true,
      });
    },
    [resourceType, uniqueId],
  );

  const fetchProjectAndUpdateTree = useCallback(async () => {
    const path = `${DBT_ASSETS_ROOT_NODE_NAME}/${projectId}`;
    // set the parent to show the loading state
    setDbtTree((prev) => {
      const newItems = [...prev];
      const updatedNode = newItems.find(
        (node) => node.id === DBT_ASSETS_ROOT_NODE_NAME,
      );
      if (!updatedNode) return prev;
      updatedNode.data.pagination = {
        hasMore: updatedNode.data.pagination?.hasMore ?? false,
        page: updatedNode.data.pagination?.page ?? 0,
        total: updatedNode.data.pagination?.total ?? 0,
        isLoading: true,
      };
      return newItems;
    });

    const projectResponse = await getProject({
      accountId: accountId ?? 0,
      projectId: projectId!,
      includeRelated: ['environments'],
    });

    setAllProjects((prev) => uniqBy([...prev, projectResponse.data], 'id'));

    const fileTreeItems = generateProjectFileTreeItems([projectResponse.data]);

    const environmentIds = getDefaultEnvironmentIds(
      projectResponse.data?.environments ?? [],
      [selectedEnvironmentType],
    );

    setTreeStateForPath(path, { isLoading: true, hasMore: false });
    openDirectoryIfNotOpen(path);
    const resourcesFileTreeItems = await generateResourceFileTreeItems(
      path,
      environmentIds,
    );
    setDbtTree((prev) => {
      const newItems = uniqBy(
        [...prev, ...fileTreeItems, ...resourcesFileTreeItems],
        'id',
      );
      const rootNode = newItems.find((node) => node.id === DBT_ASSETS_ROOT_NODE_NAME);
      if (!rootNode) return prev;
      rootNode.data.pagination = {
        hasMore: rootNode.data.pagination?.hasMore ?? false,
        page: rootNode.data.pagination?.page ?? 0,
        total: rootNode.data.pagination?.total ?? 0,
        isLoading: false,
      };
      return newItems;
    });
    navigateAndScrollToFile(path);
    setIsInitialized(true);
  }, [
    projectId,
    getProject,
    accountId,
    setAllProjects,
    generateProjectFileTreeItems,
    selectedEnvironmentType,
    setTreeStateForPath,
    openDirectoryIfNotOpen,
    generateResourceFileTreeItems,
    navigateAndScrollToFile,
  ]);

  const selectProjectAndGenerateChildren = useCallback(async () => {
    const path = `${DBT_ASSETS_ROOT_NODE_NAME}/${projectId}`;
    openDirectoryIfNotOpen(path);
    setTreeStateForPath(path, { isLoading: true, hasMore: false });
    const fileTreeItems: FileTreeItemType[] = [];
    const environmentIds = getDefaultEnvironmentIds(
      allProjects.find((project) => project.id === projectId)?.environments ?? [],
      [selectedEnvironmentType],
    );

    // if the folder chidlren are not loaded, we need to load them
    if (!dbtTree.some((node) => node.parent === path)) {
      fileTreeItems.push(
        ...(await generateResourceFileTreeItems(path, environmentIds)),
      );
    }
    setDbtTree((prev) => {
      const newItems = uniqBy([...prev, ...fileTreeItems], 'id');
      const updatedNode = newItems.find((node) => node.id === path);
      if (!updatedNode) return newItems;
      updatedNode.data.pagination = {
        hasMore: updatedNode.data.pagination?.hasMore ?? false,
        page: updatedNode.data.pagination?.page ?? 0,
        total: updatedNode.data.pagination?.total ?? 0,
        isLoading: false,
      };
      return newItems;
    });
    navigateAndScrollToFile(path);
    setIsInitialized(true);
  }, [
    allProjects,
    dbtTree,
    generateResourceFileTreeItems,
    navigateAndScrollToFile,
    openDirectoryIfNotOpen,
    projectId,
    selectedEnvironmentType,
    setTreeStateForPath,
  ]);

  const buildTree = useCallback(() => {
    const path = `${DBT_ASSETS_ROOT_NODE_NAME}/${projectId}`;
    const parsedResourceType = getResourceType(resourceType);
    // this will only trigger if we have no selection (first load or coming from other page)
    // or if we are switcing projects (checking both file and folder)
    if (
      (!selectedFile && !selectedFolder) ||
      (selectedFolder && !selectedFolder.includes(projectId?.toString() ?? '')) ||
      (selectedFile && !selectedFile.includes(projectId?.toString() ?? ''))
    ) {
      const project = allProjects.find((project) => project.id === projectId);
      // project exists, so we can open the folder
      if (project) {
        selectProjectAndGenerateChildren();
        // if project does not exist, we need to fetch it
      } else {
        fetchProjectAndUpdateTree();
      }
      // if we have a resource type and it's different from the current selection, we need to update the selected file
    } else if (
      parsedResourceType &&
      ((selectedFile && !selectedFile.includes(parsedResourceType)) || !selectedFile)
    ) {
      const resourcePath = `${path}/${parsedResourceType}`;
      setSelectedFile(resourcePath);
      scrollToPath(resourcePath);
      setIsInitialized(true);
      // if we have no resource type, no details id, and we are not navigating to another project,
      // we set the selected folder to be the path (which is the current project)
    } else if (!parsedResourceType && !uniqueId) {
      setSelectedFolder(path);
      scrollToPath(path);
      setIsInitialized(true);
    }
  }, [
    allProjects,
    fetchProjectAndUpdateTree,
    projectId,
    resourceType,
    selectProjectAndGenerateChildren,
    selectedFile,
    selectedFolder,
    uniqueId,
  ]);

  // initialize the tree
  useEffect(() => {
    if (
      !selectedFile &&
      !selectedFolder &&
      !isInitialized &&
      isActive &&
      projectsLoaded
    ) {
      const fileTreeItems = generateProjectFileTreeItems(allProjects);
      setDbtTree((prev) => mergeTreeItems(prev, fileTreeItems));
      if (projectId) {
        const project = allProjects.find((project) => project.id === projectId);
        // project exists, so we can open the folder
        if (project) {
          selectProjectAndGenerateChildren();
          // if project does not exist, we need to fetch it
        } else {
          fetchProjectAndUpdateTree();
        }
      } else {
        setSelectedFolder(DBT_ASSETS_ROOT_NODE_NAME);
        setIsInitialized(true);
      }
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, isActive, projectsLoaded]);

  // whenever projectId, resourceType or active status change, we build the tree
  useEffect(() => {
    if (projectId && projectsLoaded && isActive && isInitialized) {
      buildTree();
      // if we don't have the projectId or if it's not active, we clear the selection
    } else if (!isActive) {
      setSelectedFile(undefined);
      setSelectedFolder(undefined);
    }
    // we only want this to be triggered when the respective attrs change
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, projectsLoaded, resourceType, isActive]);

  return {
    fileTree: dbtTree,
    setFileTree: setDbtTree,
    isLoading: projects.fetching,
    initialized: projectsLoaded,
    generateChildrenForPath,
    openDirectories,
    setOpenDirectories,
    selectedFile,
    setSelectedFile: handleSetSelectedFile,
    selectedFolder,
    setSelectedFolder: handleSetSelectedFolder,
    onSort,
  };
};
